import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { chatApi, ChatConversationScope, ChatPageArgs } from "@/services/chat";
import type { ChangePage, ConversationView, CursorPage, MessagePage, MessageView, Revision } from "@/interfaces/Chat";
import type { AppDispatch, RootState } from "@/store";

export type ChatConnectionState = "connected" | "disconnected" | "paused" | "blocked";
type Args = {
  actorId: string | null; conversationId: string | null;
  onChanges: (page: ChangePage, conversationId: string) => void;
  onAccessLost: (error: unknown, conversationId: string) => void;
};
export type ChatHistoryGap = { after: Revision; beforeMessageId: string };
type HistoryRange = { start: Revision; end: Revision };
type History = {
  coverage: HistoryRange[];
  version: number; messages: Record<string, MessageView>; snapshots: Record<string, Revision>;
  revision: Revision; cursor: Revision; before: Revision | null; after: Revision | null;
  conversation?: ConversationView;
};
type ListArgs = ChatPageArgs & { scope?: ChatConversationScope };
type List = CursorPage<ConversationView> & { version: number; args: ListArgs[]; conversationVersions: Record<string, number> };
type Local = { actorId: string | null; generation: number; histories: Record<string, History>; lists: Partial<Record<ChatConversationScope, List>> };
const emptyHistory = (version: number): History => ({ version, coverage: [], messages: {}, snapshots: {}, revision: "0", cursor: "0", before: null, after: null });
/** Decimal bigint comparison without lossy Number conversion or ES2020 BigInt. */
export const compareChatRevision = (a: Revision, b: Revision) => a.length === b.length ? (a === b ? 0 : a > b ? 1 : -1) : a.length > b.length ? 1 : -1;
/** A range certifies fetched coverage, never arithmetic adjacency of event revisions. */
const coverHistory = (coverage: HistoryRange[], start: Revision, end: Revision): HistoryRange[] => {
  if (compareChatRevision(start, end) > 0) return coverage;
  const merged: HistoryRange[] = [];
  [...coverage, { start, end }].sort((a,b) => compareChatRevision(a.start,b.start)).forEach(range => {
    const previous = merged[merged.length - 1];
    if (previous && compareChatRevision(range.start, previous.end) <= 0) {
      if (compareChatRevision(range.end, previous.end) > 0) previous.end = range.end;
    } else merged.push({ ...range });
  });
  return merged;
};
const coverMessagePage = (history: History, page: MessagePage, request: {before?: Revision; after?: Revision}) => {
  const first = page.data[0]?.sequence, last = page.data[page.data.length - 1]?.sequence;
  // The request pivot is included: the returned page is contiguous with it.
  // A null edge certifies the start/end at this snapshot, including empty pages.
  const start = request.after ?? (page.before === null ? '0' : first);
  const end = request.before ?? (page.after === null ? page.revision : last);
  return start && end ? coverHistory(history.coverage, start, end) : history.coverage;
};
const newerConversation = (incoming: ConversationView, previous?: ConversationView) =>
  previous && compareChatRevision(previous.revision, incoming.revision) > 0 ? previous : incoming;
const currentConversationSnapshot = (value: Local, scope: ChatConversationScope, incoming: ConversationView, versions: Record<string, number>) => {
  const list = value.lists[scope];
  const history = value.histories[incoming.id];
  const version = versions[incoming.id] ?? 0;
  const listed = (list?.conversationVersions[incoming.id] ?? 0) === version ? list?.data.find((c) => c.id === incoming.id) : undefined;
  const detail = history?.version === version ? history.conversation : undefined;
  return newerConversation(newerConversation(incoming, listed), detail);
};
const available = () => !document.hidden && navigator.onLine !== false;
const statusOf = (error: unknown) => (error as { status?: number | string })?.status;
const mergeMessages = (history: History, messages: MessageView[], snapshot: Revision) => {
  const next = { ...history, messages: { ...history.messages }, snapshots: { ...history.snapshots } };
  messages.forEach((message) => {
    // A newer full snapshot can be returned on an earlier event page. Preserve
    // every unseen ID, while never replacing its newer reactions with old data.
    if (!next.messages[message.id] || compareChatRevision(snapshot, next.snapshots[message.id]) >= 0) {
      next.messages[message.id] = message; next.snapshots[message.id] = snapshot;
    }
  });
  if (compareChatRevision(snapshot, next.revision) > 0) next.revision = snapshot;
  return next;
};

/** One serial schedule, retained for its owner's lifetime. */
const createChatPoll = (base: number, valid: () => boolean, work: () => Promise<void>, outcome: (failed: boolean) => void) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let busy = false; let queued = false; let disposed = false; let errors = 0;
  const pause = () => { if (timer) clearTimeout(timer); timer = undefined; };
  const schedule = (delay = base) => { pause(); if (!disposed && available()) timer = setTimeout(run, delay); };
  const run = async () => {
    if (disposed || !valid() || !available()) return;
    if (busy) { queued = true; return; }
    pause(); busy = true;
    try { await work(); if (!disposed && valid()) { errors = 0; outcome(false); } }
    catch (error) {
      if (!disposed && valid() && statusOf(error) !== "CUSTOM_ERROR" && ![401, 403, 404].includes(statusOf(error) as number)) { errors += 1; outcome(true); }
    } finally {
      busy = false;
      const delay = errors ? Math.min(base * (errors === 1 ? 2 : errors === 2 ? 4 : 10), 30000) : base;
      if (!disposed && valid() && queued && available()) { queued = false; void run(); }
      else if (!disposed && valid()) schedule(delay);
    }
  };
  schedule();
  return { run, pause, dispose: () => { disposed = true; pause(); } };
};

/** Mount once in ChatProvider. Owns polling, normalized history and list pages. */
export const useChatSync = ({ actorId, conversationId, onChanges, onAccessLost }: Args) => {
  const store = useStore<RootState>();
  const dispatch = store.dispatch as AppDispatch;
  const session = useSelector((state: RootState) => state.chatSession);
  const { generation, blocked, conversationVersions, conversationListVersions, refreshVersion } = session;
  const [visible, setVisible] = useState(available);
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const [local, setLocal] = useState<Local>(() => ({ actorId, generation, histories: {}, lists: {} }));
  const localRef = useRef(local); localRef.current = local;
  const callbacks = useRef({ onChanges, onAccessLost }); callbacks.current = { onChanges, onAccessLost };
  const navigation = useRef({ actorId, generation, conversationId, epoch: 0 });
  if (navigation.current.actorId !== actorId || navigation.current.generation !== generation || navigation.current.conversationId !== conversationId) {
    navigation.current = { actorId, generation, conversationId, epoch: navigation.current.epoch + 1 };
  }
  const pending = useRef(new Map<string, { abort: () => void; unsubscribe: () => void; promise: Promise<unknown>; identity: string }>());
  const listIntents = useRef<Record<ChatConversationScope, number>>({ mine: 0, others: 0 });
  const messageIntent = useRef(0);
  const refreshCollections = useRef<() => void>(() => {});
  const deltaPoll = useRef<ReturnType<typeof createChatPoll> | null>(null);
  const refresh = useRef<() => void>(() => {});
  const summary = useSelector((state: RootState) => chatApi.endpoints.getChatSummary.select({ actorId: actorId ?? "" })(state).data);
  const current = useCallback((id?: string, scope?: ChatConversationScope) => {
    const state = store.getState();
    return !!actorId && state.auth.user?.id === actorId && state.chatSession.generation === generation && !state.chatSession.blocked &&
      (!id || (state.chatSession.conversationVersions[id] ?? 0) === (conversationVersions[id] ?? 0)) &&
      (!scope || state.chatSession.conversationListVersions[scope] === conversationListVersions[scope]);
  }, [store, actorId, generation, conversationVersions, conversationListVersions]);
  const reportAccessLost = useCallback((id: string, epoch: number, error?: unknown) => {
    const state = store.getState();
    if (navigation.current.epoch === epoch && state.auth.user?.id === actorId && state.chatSession.generation === generation &&
      (state.chatSession.conversationVersions[id] ?? 0) !== (conversationVersions[id] ?? 0)) {
      callbacks.current.onAccessLost(error ?? { status: "CUSTOM_ERROR", error: "CHAT_ACCESS_LOST" }, id);
    }
  }, [store, actorId, generation, conversationVersions]);
  const write = useCallback((update: (value: Local) => Local) => {
    setLocal((previous) => {
      if (!current()) return previous;
      const base = previous.actorId === actorId && previous.generation === generation ? previous : { actorId, generation, histories: {}, lists: {} };
      const next = update(base); localRef.current = next; return next;
    });
  }, [actorId, generation, current]);
  // Manual loaders share this lock with polling. Each request is abortable and
  // unsubscribed immediately after settlement (no second history subscription).
  const request = useCallback(<T,>(key: string, start: () => { unwrap: () => Promise<T>; abort: () => void; unsubscribe: () => void }, identity = key): Promise<T> => {
    const running = pending.current.get(key);
    if (running?.identity === identity) return running.promise as Promise<T>;
    if (running) { running.abort(); running.unsubscribe(); pending.current.delete(key); }
    const startedSession = store.getState();
    const result = start();
    const promise = result.unwrap().finally(() => {
      result.unsubscribe();
      if (pending.current.get(key)?.promise === promise) pending.current.delete(key);
      const now = store.getState();
      if ((key === "summary" || key === "list") && now.auth.user?.id === startedSession.auth.user?.id &&
        now.chatSession.generation === startedSession.chatSession.generation && now.chatSession.refreshVersion > startedSession.chatSession.refreshVersion) refreshCollections.current();
    });
    pending.current.set(key, { abort: result.abort, unsubscribe: result.unsubscribe, promise, identity });
    return promise;
  }, [store]);
  const loadMessages = useCallback(async (page: { before?: Revision; after?: Revision; around?: string; limit?: number } = {}) => {
    const id = conversationId; const epoch = navigation.current.epoch; const intent = ++messageIntent.current;
    if (!actorId || !id || !current(id) || !available()) return;
    try {
      const result = await request<MessagePage>("messages", () => dispatch(chatApi.endpoints.getChatMessages.initiate({ actorId, conversationId: id, ...page }, { subscribe: false, forceRefetch: true })), JSON.stringify(page));
      if (!current(id) || navigation.current.epoch !== epoch || messageIntent.current !== intent) { reportAccessLost(id, epoch); return; }
      write((value) => {
        const previous = value.histories[id];
        let history = mergeMessages(previous?.version === (conversationVersions[id] ?? 0) ? previous : emptyHistory(conversationVersions[id] ?? 0), result.data, result.revision);
        history = { ...history, coverage: coverMessagePage(history, result, page), before: page.around || !page.after ? result.before : history.before, after: page.before ? history.after : result.after };
        // Only initial history establishes the delta baseline. Later page loads
        // must never jump over events the incremental reader has not consumed.
        if (history.cursor === "0") history.cursor = result.revision;
        return { ...value, histories: { ...value.histories, [id]: history } };
      });
      setFailed((v) => ({ ...v, messages: false }));
      return result;
    } catch (error) {
      if (messageIntent.current === intent) reportAccessLost(id, epoch, error);
      if (current(id) && navigation.current.epoch === epoch && messageIntent.current === intent && statusOf(error) !== "CUSTOM_ERROR") setFailed((v) => ({ ...v, messages: true }));
      return undefined;
    }
  }, [actorId, conversationId, current, dispatch, request, write, conversationVersions, reportAccessLost]);
  const loadConversations = useCallback(async (args: ListArgs = {}) => {
    const scope = args.scope ?? "mine"; const intent = ++listIntents.current[scope];
    if (!actorId || !current(undefined, scope) || !available()) return;
    try {
      const result = await request<CursorPage<ConversationView>>("list", () => dispatch(chatApi.endpoints.getChatConversations.initiate({ actorId, ...args }, { subscribe: false, forceRefetch: true })), JSON.stringify({ ...args, scope }));
      if (!current(undefined, scope) || listIntents.current[scope] !== intent) return;
      write((value) => {
        const previous = value.lists[scope];
        const append = !!args.cursor && previous?.version === conversationListVersions[scope] && previous.args[0]?.q === args.q;
        const data = new Map((append ? previous!.data : []).map((item) => [item.id, item]));
        result.data.forEach((item) => data.set(item.id, currentConversationSnapshot(value, scope, item, conversationVersions)));
        const pages = append ? [...previous!.args.filter((p) => p.cursor !== args.cursor), args] : [args];
        return { ...value, lists: { ...value.lists, [scope]: { ...result, data: Array.from(data.values()), version: conversationListVersions[scope], args: pages, conversationVersions: { ...(append ? previous!.conversationVersions : {}), ...conversationVersions } } } };
      });
      setFailed((value) => ({ ...value, [`list:${scope}`]: false }));
      return result;
    } catch (error) {
      if (current(undefined, scope) && listIntents.current[scope] === intent && statusOf(error) !== "CUSTOM_ERROR") setFailed((value) => ({ ...value, [`list:${scope}`]: true }));
      return undefined;
    }
  }, [actorId, current, dispatch, request, write, conversationListVersions, conversationVersions]);

  useEffect(() => {
    setLocal((value) => value.actorId === actorId && value.generation === generation ? value : { actorId, generation, histories: {}, lists: {} });
    setFailed({});
    return () => { pending.current.forEach((p) => { p.abort(); p.unsubscribe(); }); pending.current.clear(); };
  }, [actorId, generation]);
  useEffect(() => {
    setLocal((value) => {
      if (value.actorId !== actorId || value.generation !== generation) return value;
      const histories = Object.fromEntries(Object.entries(value.histories).filter(([id, history]) => history.version === (conversationVersions[id] ?? 0)));
      const lists: Local["lists"] = {};
      (["mine", "others"] as ChatConversationScope[]).forEach((scope) => {
        const list = value.lists[scope];
        if (list?.version === conversationListVersions[scope]) lists[scope] = { ...list, data: list.data.filter((c) => (list.conversationVersions[c.id] ?? 0) === (conversationVersions[c.id] ?? 0)) };
      });
      return { ...value, histories, lists };
    });
  }, [actorId, generation, conversationVersions, conversationListVersions]);

  useEffect(() => {
    if (!actorId || blocked || !available()) return;
    let disposed = false;
    void request("summary", () => dispatch(chatApi.endpoints.getChatSummary.initiate({ actorId }, { subscribe: false, forceRefetch: true })))
      .then(() => { if (!disposed && current()) setFailed((v) => ({ ...v, summary: false })); })
      .catch((error) => { if (!disposed && current() && statusOf(error) !== "CUSTOM_ERROR") setFailed((v) => ({ ...v, summary: true })); });
    return () => { disposed = true; };
    // Initial fetch only: scope/conversation invalidation must not start a
    // duplicate summary stream; their refreshes belong to the poller below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorId, generation, blocked, dispatch, request]);

  useEffect(() => {
    // Global CRM counters survive conversation navigation; conversation data
    // requests are canceled. Session/unmount cleanup aborts every request.
    return () => { ["changes", "messages"].forEach((key) => { const p = pending.current.get(key); p?.abort(); p?.unsubscribe(); pending.current.delete(key); }); };
  }, [conversationId, actorId, generation]);

  useEffect(() => {
    if (!actorId || blocked) return;
    let disposed = false;
    const epoch = navigation.current.epoch;
    const valid = (id?: string) => !disposed && current(id) && navigation.current.epoch === epoch;
    const poll = createChatPoll(3000, () => valid(conversationId ?? undefined), async () => {
      const id = conversationId; if (!id || !valid(id)) return;
      const saved = localRef.current;
      const history = saved.actorId === actorId && saved.generation === generation ? saved.histories[id] : undefined;
      let after = history?.version === (conversationVersions[id] ?? 0) ? history.cursor : "0";
      let more = true;
      while (more && valid(id) && available()) {
        try {
          const page = await request<ChangePage>("changes", () => dispatch(chatApi.endpoints.getChatChanges.initiate({ actorId, conversationId: id, after }, { subscribe: false, forceRefetch: true })));
          if (!valid(id)) { reportAccessLost(id, epoch); return; }
          write((value) => {
            const previous = value.histories[id];
            const base = previous?.version === (conversationVersions[id] ?? 0) ? previous : emptyHistory(conversationVersions[id] ?? 0);
            const next = mergeMessages(base, page.messages, page.conversation.revision);
            // Only the consumed event interval is complete. Sparse reaction snapshots
            // elsewhere in page.messages do not certify intervening history.
            if (compareChatRevision(page.cursor, after) > 0) next.coverage = coverHistory(next.coverage, after, page.cursor);
            if (!next.conversation || compareChatRevision(page.conversation.revision, next.conversation.revision) >= 0) next.conversation = page.conversation;
            if (compareChatRevision(page.cursor, next.cursor) > 0) next.cursor = page.cursor;
            const lists = { ...value.lists };
            (["mine", "others"] as ChatConversationScope[]).forEach((scope) => {
              const list = lists[scope];
              if (list) lists[scope] = { ...list, data: list.data.map((c) => c.id === id ? newerConversation(page.conversation, c) : c) };
            });
            return { ...value, histories: { ...value.histories, [id]: next }, lists };
          });
          if (page.membersChanged) dispatch(chatApi.util.invalidateTags([{ type: "Members", id: `${actorId}:${id}` }]));
          callbacks.current.onChanges(page, id);
          // Snapshot revision is deliberately never used as an event cursor.
          more = page.hasMore && compareChatRevision(page.cursor, after) > 0;
          after = page.cursor;
        } catch (error) {
          // Purging can remove an RTK result before unwrap exposes its error;
          // the invalidation generation is the authoritative access-loss signal.
          if (!disposed) reportAccessLost(id, epoch, error);
          throw error;
        }
      }
    }, (error) => setFailed((value) => ({ ...value, changes: error })));
    deltaPoll.current = poll;
    return () => { disposed = true; poll.dispose(); if (deltaPoll.current === poll) deltaPoll.current = null; };
  }, [actorId, conversationId, generation, blocked, current, dispatch, store, request, write, conversationVersions, reportAccessLost]);

  // Read fresh scope guards without tying the global scheduler's lifetime to
  // navigation or conversation/scope invalidations.
  const collectionView = useRef({ current, write, conversationVersions, conversationListVersions });
  collectionView.current = { current, write, conversationVersions, conversationListVersions };
  useEffect(() => {
    if (!actorId || blocked) return;
    const valid = () => {
      const state = store.getState();
      return state.auth.user?.id === actorId && state.chatSession.generation === generation && !state.chatSession.blocked;
    };
    const collections = createChatPoll(10000, valid, async () => {
      await request("summary", () => dispatch(chatApi.endpoints.getChatSummary.initiate({ actorId }, { subscribe: false, forceRefetch: true })));
      if (!valid() || !available()) return;
      // RTK refetches subscribed inbox pages only; their query rebuilds fresh
      // cursors and the scoped base query rejects late session results.
      dispatch(chatApi.util.invalidateTags([{ type: "Mentions", id: actorId }]));
      const { current, write, conversationVersions, conversationListVersions } = collectionView.current;
      // Refresh every loaded filter/page; retain the normalized list until the
      // complete refresh succeeds. RTK handles deduplication with mounted views.
      for (const scope of ["mine", "others"] as ChatConversationScope[]) {
        const list = localRef.current.lists[scope];
        if (!list || !current(undefined, scope) || list.version !== conversationListVersions[scope]) continue;
        const intent = listIntents.current[scope];
        const pages: CursorPage<ConversationView>[] = [];
        const chain: ListArgs[] = [];
        const { cursor: _oldCursor, ...firstArgs } = list.args[0];
        let args: ListArgs = firstArgs;
        for (let index = 0; index < list.args.length; index++) {
          chain.push(args);
          const page = await request<CursorPage<ConversationView>>("list", () => dispatch(chatApi.endpoints.getChatConversations.initiate({ actorId, ...args }, { subscribe: false, forceRefetch: true })), JSON.stringify({ ...args, scope }));
          pages.push(page);
          if (!valid() || !available() || !current(undefined, scope) || listIntents.current[scope] !== intent) return;
          if (!page.nextCursor) break;
          args = { ...firstArgs, cursor: page.nextCursor };
        }
        setFailed((value) => ({ ...value, [`list:${scope}`]: false }));
        write((value) => ({ ...value, lists: { ...value.lists, [scope]: { ...list, args: chain, conversationVersions, data: Array.from(new Map(pages.flatMap((p) => p.data).map((c) => [c.id, currentConversationSnapshot(value, scope, c, conversationVersions)])).values()), nextCursor: pages[pages.length - 1].nextCursor } } }));
      }
    }, (error) => setFailed((value) => ({ ...value, collections: error, ...(!error ? { summary: false } : {}) })));
    const resume = () => { setVisible(available()); collections.pause(); deltaPoll.current?.pause(); if (available()) { void deltaPoll.current?.run(); void collections.run(); } };
    const pause = () => { setVisible(available()); if (!available()) { collections.pause(); deltaPoll.current?.pause(); } };
    const visibility = () => available() ? resume() : pause();
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("focus", resume); window.addEventListener("online", resume); window.addEventListener("offline", pause);
    refreshCollections.current = () => { void collections.run(); };
    refresh.current = () => { void deltaPoll.current?.run(); void collections.run(); };
    return () => {
      collections.dispose();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("focus", resume); window.removeEventListener("online", resume); window.removeEventListener("offline", pause);
      refreshCollections.current = () => {}; refresh.current = () => {};
    };
  }, [actorId, generation, blocked, dispatch, store, request]);
  const lastRefresh = useRef(refreshVersion);
  useEffect(() => { if (lastRefresh.current !== refreshVersion) { lastRefresh.current = refreshVersion; refresh.current(); } }, [refreshVersion]);
  const usable = local.actorId === actorId && local.generation === generation && !blocked;
  const history = usable && conversationId && local.histories[conversationId]?.version === (conversationVersions[conversationId] ?? 0) ? local.histories[conversationId] : undefined;
  const messages = useMemo(() => history ? Object.values(history.messages).sort((a, b) => compareChatRevision(a.sequence, b.sequence)) : [], [history]);
  const historyGaps = useMemo(() => {
    const gaps: ChatHistoryGap[] = [];
    if (history) messages.forEach((message, index) => {
      const previous = messages[index - 1];
      if (previous && !history.coverage.some(range => compareChatRevision(range.start, previous.sequence) <= 0 && compareChatRevision(range.end, message.sequence) >= 0))
        gaps.push({ after: previous.sequence, beforeMessageId: message.id });
    });
    return gaps;
  }, [history, messages]);
  const firstMessage = messages[0], lastMessage = messages[messages.length - 1];
  const before = history && firstMessage && !history.coverage.some(range => range.start === '0' && compareChatRevision(range.end, firstMessage.sequence) >= 0) ? firstMessage.sequence : null;
  const after = history && lastMessage && !history.coverage.some(range => compareChatRevision(range.start, lastMessage.sequence) <= 0 && compareChatRevision(range.end, history.revision) >= 0) ? lastMessage.sequence : null;
  const conversationLists = useMemo(() => {
    const lists: Partial<Record<ChatConversationScope, CursorPage<ConversationView>>> = {};
    if (usable) (["mine", "others"] as ChatConversationScope[]).forEach((scope) => {
      const list = local.lists[scope];
      if (list?.version === conversationListVersions[scope]) lists[scope] = { data: list.data.filter((c) => (list.conversationVersions[c.id] ?? 0) === (conversationVersions[c.id] ?? 0)), nextCursor: list.nextCursor };
    });
    return lists;
  }, [usable, local, conversationListVersions, conversationVersions]);
  const connectionState: ChatConnectionState = blocked ? "blocked" : !visible ? "paused" : Object.values(failed).some(Boolean) ? "disconnected" : "connected";
  const refreshChat = useCallback(() => { if (current()) refresh.current(); }, [current]);
  return { messages, messagesById: history?.messages ?? {}, revision: history?.revision ?? "0", cursor: history?.cursor ?? "0",
    before, after, historyGaps, conversation: history?.conversation ?? null,
    summary: !blocked ? summary ?? null : null, conversationLists, connectionState, loadMessages, loadConversations,
    refreshChat };
};
